import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Check, Briefcase, HandHeart, Sparkles } from "lucide-react";
import useSeo from "../hooks/useSeo";

const ROLES = [
  { id: "paid-gigs", label: "Paid gigs", icon: Briefcase, note: "Hosting, photography, content, design, ops" },
  { id: "volunteer", label: "Volunteering", icon: HandHeart, note: "Help host evenings, run errands, lend a space" },
  { id: "either", label: "Either / both", icon: Sparkles, note: "Open to wherever I am most useful" },
];
const CITIES = ["Hyderabad", "Bangalore", "Pune"];

export default function Join() {
  useSeo({
    title: "Gig with us — saamne",
    description:
      "Paid gigs and volunteering with saamne in Hyderabad, Bangalore and Pune. Hosting, photography, content, ops, lending a space — write to us and we'll reply within a week.",
    path: "/join",
  });
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "Hyderabad", category: "either", comments: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone, please.");
      return;
    }
    setSending(true);
    try {
      await api.post("/interests", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        category: form.category,
        city: form.city,
        comments: form.comments.trim() || null,
      });
      setDone(true);
      toast.success("Thanks. We read every note and reply within a week.");
    } catch { toast.error("Something went wrong. Try again."); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="join-page" className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-24">
      <div className="label-eyebrow">Gig with us</div>
      <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight">
        Host with <em style={{ color: "var(--highlight)" }}>us</em>.
      </h1>
      <p className="mt-5 text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
        We're a small, growing crew. If you care about the kind of in-person world we're trying to build — for a paid gig, or to volunteer — we'd love to hear from you.
      </p>

      <section className="mt-10 space-y-4 text-base leading-relaxed" style={{ color: "var(--text-main)" }}>
        <h2 className="font-serif text-2xl md:text-3xl leading-tight">Who we're looking for</h2>
        <p>
          We're slowly building a small bench of people across Hyderabad,
          Bangalore and Pune — <strong>hosts</strong> who can hold a room and
          make twelve strangers feel like neighbours, <strong>photographers</strong>{" "}
          who shoot quietly, <strong>writers</strong> who can pen a six-line
          invite that doesn't feel like marketing, <strong>operations folk</strong>{" "}
          who can scout venues and chase suppliers, and <strong>volunteers</strong>{" "}
          who simply want to be part of the evenings we host. You don't need
          a fancy resume. You need taste, patience, and the willingness to
          actually show up.
        </p>
        <p>
          Paid gigs are engagement-based for now — a single evening, a weekend
          getaway, a small lifeskill workshop. We pay fairly and on time.
          Volunteering is open-ended; we ask for a few hours a month and try
          to give back in dinners, tickets, and good company. Either way, we
          read every note personally, and we reply within a week on the
          contact you leave with us.
        </p>
      </section>

      <div className="mt-12">
        {done ? (
          <div data-testid="join-success" className="card-warm p-10 text-center">
            <div className="mx-auto mb-5 flex items-center justify-center rounded-full"
                 style={{ width: 64, height: 64, background: "var(--surface)", color: "var(--primary)" }}>
              <Check size={32} />
            </div>
            <h3 className="font-serif text-3xl">Note received.</h3>
            <p className="mt-3 text-sm" style={{ color: "var(--text-muted)" }}>
              We read every one personally. Expect a real reply within a week.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="join-form" className="form-card space-y-5">
            <div>
              <div className="label-eyebrow mb-3">I'm here for…</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="role-picker">
                {ROLES.map((r) => {
                  const active = form.category === r.id;
                  return (
                    <button key={r.id} type="button" onClick={() => setForm({ ...form, category: r.id })}
                            data-testid={`role-${r.id}`} data-active={active}
                            className="tile-select">
                      <r.icon size={20} />
                      <div className="font-serif text-lg mt-2 leading-snug">{r.label}</div>
                      <div className="text-xs mt-1" style={{ opacity: 0.85 }}>{r.note}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-3">City</div>
              <div className="grid grid-cols-3 gap-2" data-testid="city-picker">
                {CITIES.map((c) => {
                  const active = form.city === c;
                  return (
                    <button key={c} type="button" onClick={() => setForm({ ...form, city: c })}
                            data-active={active} data-testid={`pick-city-${c.toLowerCase()}`}
                            className="pill-select">{c}</button>
                  );
                })}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="form-name" />
              <input type="tel" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="form-phone" />
            </div>
            <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
            <textarea rows={4} placeholder="A few lines — what draws you to saamne? What would you love to do with us?" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} data-testid="form-comments" />
            <button type="submit" disabled={sending} className="btn-primary w-full justify-center" data-testid="form-submit">
              {sending ? "Sending…" : "Send my interest"} <Send size={16} />
            </button>
            <p className="fine-note text-center">
              Real reply, from a real person, within a week.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
